import React, { useEffect, useRef, useState } from 'react';

import { Check, ArrowRight, CircleX } from 'lucide-react';
import { toast } from 'react-toastify/unstyled';

import { useRegenerateAgentPackageLinkMutation } from '@features/integrations/api/create-agent.api';
import { GenericButton } from '@shared/components/ui/custom';
import { ModalOverlay } from '@shared/overlays/error-overlays/ModalOverlay';

import { DownloadAgentButton, ShareAgentButton } from './create-agent-download.utils';

interface DownloadAgentPackageProps {
  agentId: string;
  downloadLink: string;
  setDownloadLink: (link: string) => void;
  onClose: () => void;
}

const ERROR_LIMIT_THRESHOLD = 3;
export type DownloadStatus = 'idle' | 'loading' | 'success' | 'error';

export const DownloadAgentPackage: React.FC<DownloadAgentPackageProps> = ({
  agentId,
  downloadLink,
  setDownloadLink,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [showRegeneratedDownloadMsg, setShowRegeneratedDownloadMsg] = useState(false);

  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('idle');
  const [downloadErrorCount, setDownloadErrorCount] = useState(0);

  const isSuccess = downloadStatus === 'success';
  const isLoading = downloadStatus === 'loading';
  const isError = downloadStatus === 'error';
  const isInteractionDisabled = isLoading || isSuccess;
  const showCopiedText = copied && !isSuccess;

  const shareTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [
    regeneratePackageLink,
    { isLoading: isRegenerating, isSuccess: isRegenerateSuccess, isError: isRegenerateError, reset: resetRegenerate },
  ] = useRegenerateAgentPackageLinkMutation();

  useEffect(() => {
    if (!isRegenerateSuccess) return;
    setShowRegeneratedDownloadMsg(true);

    const timer = setTimeout(() => {
      setShowRegeneratedDownloadMsg(false);
    }, 6000);

    return () => clearTimeout(timer);
  }, [isRegenerateSuccess]);

  useEffect(() => {
    return () => {
      if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
    };
  }, []);

  if (isRegenerateError) {
    return (
      <ModalOverlay
        isError={true}
        errorMessage="Error regenerating the package. Please try again."
        refetch={() => regeneratePackageLink({ agentId })}
      />
    );
  }

  const handleShare = async () => {
    try {
      if (!navigator.clipboard || !globalThis.isSecureContext) {
        throw new Error('Clipboard API is not available in this browser environment');
      }

      await navigator.clipboard.writeText(downloadLink);
      setCopied(true);

      if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);

      shareTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        shareTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Browser blocked automatic copying. Please copy the link from the input field above.');
    }
  };

  const handleDownload = async () => {
    if (isLoading || isSuccess) return;

    if (!downloadLink || downloadLink.trim() === '') {
      console.error('Download triggered but downloadLink is empty.');
      setDownloadStatus('error');
      setDownloadErrorCount((prev) => prev + 1);
      return;
    }

    let blobUrl: string | undefined = undefined;

    try {
      setDownloadStatus('loading');
      setShowRegeneratedDownloadMsg(false);

      const response = await fetch(downloadLink);
      if (!response.ok) {
        throw new Error(`HTTP download error! status: ${response.status}`);
      }

      const blob = await response.blob();
      blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'standalone-agent-bundle.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();

      setDownloadStatus('success');
      setDownloadErrorCount(0);
    } catch (error) {
      console.error('Deployment bundle download failed:', error);
      setDownloadStatus('error');
      setDownloadErrorCount((prev) => prev + 1);
    } finally {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    }
  };

  const onRegeneratePackageLink = async () => {
    if (!agentId) {
      toast.error('Agent ID is missing. Cannot regenerate bundle.');
      return;
    }
    try {
      const response = await regeneratePackageLink({ agentId }).unwrap();
      const newLink = response?.bundleLink ? String(response.bundleLink) : '';

      setDownloadLink(newLink);
      setDownloadStatus('idle');
      setDownloadErrorCount(0);
      setCopied(false);
    } catch (error) {
      console.error('Error regenerating package:', error);
      toast.error('Error regenerating the package. Please try again.');
    }
  };

  const handleCloseModal = () => {
    resetRegenerate();
    onClose();
  };

  return (
    <div className="w-full max-w-[640px] px-1 flex flex-col gap-6">
      <div className="bg-zinc-900 border border-gray-800 rounded-lg p-5 flex flex-col gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-200">Download Link</h4>
          <p className="text-xs text-gray-500 mt-1">
            This one-time link contains the agent and configuration file. Click to download or share it internally.
          </p>
        </div>

        <div className="flex flex-col gap-3 mt-1">
          <input
            id="agent-download-link"
            name="agentDownloadLink"
            type="text"
            readOnly
            value={downloadLink}
            disabled={isInteractionDisabled}
            onClick={(e) => e.currentTarget.select()}
            className="w-full bg-zinc-800 border border-gray-700 rounded px-3 py-2 text-xs text-gray-400 focus:outline-none focus:border-gray-500 transition cursor-text select-all disabled:cursor-not-allowed disabled:opacity-50"
          />

          <div className="flex items-center w-full">
            <div className="flex-1 flex items-center justify-end pr-2">
              {showRegeneratedDownloadMsg && (
                <div className="text-[11px] text-emerald-400 font-medium flex items-center gap-1 animate-in fade-in slide-in-from-left-2 duration-300">
                  <Check size={14} className="text-emerald-400" />
                  <span>New download link generated!</span>
                  <ArrowRight size={14} />
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <DownloadAgentButton
                isLoading={isLoading}
                isSuccess={isSuccess}
                isInteractionDisabled={isInteractionDisabled}
                handleDownload={handleDownload}
              />
              <ShareAgentButton
                isSuccess={isSuccess}
                showCopiedText={showCopiedText}
                isInteractionDisabled={isInteractionDisabled}
                handleShare={handleShare}
              />
            </div>
          </div>

          {downloadStatus === 'error' && downloadErrorCount > 0 && (
            <div className="flex flex-col items-end gap-1 ml-auto animate-in fade-in slide-in-from-left-2 duration-300">
              <div className="text-sm font-medium text-red-400 flex items-center gap-1">
                <CircleX size={14} />
                <span>Download failed! Please try again</span>
              </div>
              {downloadErrorCount >= ERROR_LIMIT_THRESHOLD && isError && (
                <div className="text-xs font-normal text-zinc-400 flex items-center gap-1">
                  <span>Having trouble? Try regenerating the package link below</span>
                </div>
              )}
            </div>
          )}

          {isSuccess && (
            <div className="text-sm text-emerald-400 font-medium flex items-center gap-1 animate-in fade-in slide-in-from-left-2 duration-300 ml-auto">
              <Check size={14} className="text-emerald-400" />
              <span>Download successful! Please check your downloads folder</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <GenericButton variant="secondary" type="button" label="Done" onClick={handleCloseModal} />
        {downloadErrorCount >= ERROR_LIMIT_THRESHOLD && isError && (
          <GenericButton
            type="button"
            isLoading={isRegenerating}
            label="Regenerate Link"
            disabled={isLoading}
            onClick={onRegeneratePackageLink}
          />
        )}
      </div>
    </div>
  );
};
